import dagre from '@dagrejs/dagre';
import type { Edge as RFEdge, Node as RFNode } from '@xyflow/react';

import type { GraphEdge, GraphNode } from '@chainlit/react-client';

export interface CRNodeData extends Record<string, unknown> {
  label: string;
  color: string;
  shape?: string;
  icon?: string;
  risk?: number;
  status?: string;
  description?: string;
  raw: Record<string, unknown>;
  hasSubnodes: boolean;
  subnodeCount: number;
  isExpanded: boolean;
  graphIdentifier: string;
  isSelected: boolean;
  direction: 'TB' | 'LR';
}

export const NODE_WIDTH = 260;
export const NODE_HEIGHT = 92;

// Task statuses that are "done" for view purposes — a branch whose leaves are all terminal
// auto-collapses, and a terminal edge is colored by status rather than left pending (#1123).
const TERMINAL_STATUSES = new Set(['COMPLETE', 'FAILED', 'SKIPPED']);

interface FlatNode {
  id: string;
  node: GraphNode;
  parentId?: string;
}

/**
 * Walk a graph by edge-derived adjacency so collapsing/expanding a node
 * honors the real parent/child relationships emitted by the backend
 * (which ships flat `cl.Node` + `cl.Edge` lists rather than nested
 * subnode arrays). Nested ``node.subnodes`` are still honored when
 * present so legacy payloads keep working.
 */
function flattenByAdjacency(
  nodes: GraphNode[],
  childrenByParent: Map<string, string[]>,
  incoming: Set<string>,
  collapsed: Set<string>
): FlatNode[] {
  const nodesById = new Map<string, GraphNode>();
  nodes.forEach((n) => nodesById.set(n.identifier, n));
  const out: FlatNode[] = [];
  const seen = new Set<string>();

  const walk = (node: GraphNode, parentId?: string) => {
    if (seen.has(node.identifier)) return;
    seen.add(node.identifier);
    out.push({ id: node.identifier, node, parentId });
    if (collapsed.has(node.identifier)) return;
    const edgeChildren = childrenByParent.get(node.identifier) ?? [];
    for (const cid of edgeChildren) {
      const child = nodesById.get(cid);
      if (child) walk(child, node.identifier);
    }
    if (Array.isArray(node.subnodes)) {
      node.subnodes.forEach((child) => walk(child, node.identifier));
    }
  };

  const roots = nodes.filter((n) => !incoming.has(n.identifier));
  if (roots.length === 0) {
    nodes.forEach((n) => walk(n));
    return out;
  }
  roots.forEach((r) => walk(r));
  nodes.forEach((n) => {
    if (!incoming.has(n.identifier) && !seen.has(n.identifier)) walk(n);
  });
  return out;
}

/**
 * Identifiers of every node whose full subtree (via edge-derived
 * parent/child adjacency) bottoms out in COMPLETE-status leaves.
 *
 * Cycle-safe: a node already on the current recursion stack (``visiting``)
 * is treated as incomplete instead of re-entered, so a cyclic edge set —
 * e.g. STIX relationship loops merged into the Operation Overview graph,
 * which are legitimate data and not something the backend can guarantee
 * acyclic the way path-keyed COA nodes are — can't recurse forever.
 */
export function computeFullyCompleteParents(
  nodes: GraphNode[],
  childrenByParent: Map<string, string[]>
): Set<string> {
  const result = new Set<string>();
  if (nodes.length === 0) return result;

  const status = new Map<string, string | undefined>();
  nodes.forEach((n) => {
    const s = (n.data as Record<string, unknown> | undefined)?.status;
    status.set(
      n.identifier,
      typeof s === 'string' ? s.toUpperCase() : undefined
    );
  });

  const cache = new Map<string, boolean>();
  const visiting = new Set<string>();
  const allLeavesComplete = (id: string): boolean => {
    const cached = cache.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return false;
    visiting.add(id);
    const kids = childrenByParent.get(id) ?? [];
    const ok =
      kids.length === 0
        ? TERMINAL_STATUSES.has(status.get(id) ?? '')
        : kids.every(allLeavesComplete);
    visiting.delete(id);
    cache.set(id, ok);
    return ok;
  };

  nodes.forEach((n) => {
    const kids = childrenByParent.get(n.identifier) ?? [];
    if (kids.length > 0 && allLeavesComplete(n.identifier)) {
      result.add(n.identifier);
    }
  });
  return result;
}

/**
 * Identifiers of every parent node that sits OFF the active/selected trail —
 * one whose subtree holds no node the backend marked ``data.on_active_path``.
 * These are the inactive sub-trees to auto-collapse so the active branch stands
 * out, mirroring how ``computeFullyCompleteParents`` folds away finished work.
 *
 * The backend stamps ``on_active_path`` on the contiguous root→active-task spine
 * (``applit._resolve_active_path_ids``), so "off the spine" is equivalent to "no
 * active/selected descendant" — a plain per-node filter, no tree walk. Returns an
 * empty set when no node BELOW the root is active (a freshly generated / idle tree,
 * where only the root carries the flag) so an unstarted plan is left fully expanded
 * rather than folded down to the root. O(n) with no recursion, so a cyclic edge set
 * can't spin the stack.
 */
export function computeInactiveParents(
  nodes: GraphNode[],
  childrenByParent: Map<string, string[]>
): Set<string> {
  const result = new Set<string>();
  if (nodes.length === 0) return result;

  const onActive = new Set<string>();
  let hasActiveBelowRoot = false;
  nodes.forEach((n) => {
    const data = n.data as Record<string, unknown> | undefined;
    if (data?.on_active_path === true) {
      onActive.add(n.identifier);
      if (data?.is_root !== true) hasActiveBelowRoot = true;
    }
  });
  if (!hasActiveBelowRoot) return result;

  nodes.forEach((n) => {
    const kids = childrenByParent.get(n.identifier) ?? [];
    if (kids.length > 0 && !onActive.has(n.identifier)) {
      result.add(n.identifier);
    }
  });
  return result;
}

/**
 * Root node identifiers of every target that is NOT the active one, for the
 * merged all-targets view. Collapsing these ids folds each non-active target down
 * to just its root so only the active target's tree stays expanded. A target's
 * root is a node with `data.is_root === true` (real root, id `<tid>:`) or
 * `data.status === 'captured'` (stub root, id `<tid>:<tid>`); its target comes from
 * `data.target_id` (falling back to the `<tid>:` identifier prefix). Returns an
 * empty set when no active target is set.
 */
export function computeNonActiveTargetRoots(
  nodes: GraphNode[],
  activeTargetId: string | null | undefined
): Set<string> {
  const result = new Set<string>();
  if (!activeTargetId) return result;
  nodes.forEach((n) => {
    const data = n.data as Record<string, unknown> | undefined;
    const isRoot = data?.is_root === true || data?.status === 'captured';
    if (!isRoot) return;
    const targetId =
      (typeof data?.target_id === 'string' && data.target_id) ||
      (n.identifier.includes(':') ? n.identifier.split(':', 1)[0] : '');
    if (targetId && targetId !== activeTargetId) {
      result.add(n.identifier);
    }
  });
  return result;
}

/**
 * Node ids belonging to the ACTIVE target's tree (its root + all descendants), for centering
 * the merged all-targets view on the active tree. Roots use the same rule as
 * `computeNonActiveTargetRoots` (`data.is_root === true` or a `status === 'captured'` stub,
 * `data.target_id === activeTargetId`); descendants come from `collectDescendants`. Returns an
 * empty set when there is no active target or its root is not present.
 */
export function activeTreeNodeIds(
  nodes: readonly GraphNode[],
  activeTargetId: string | null | undefined,
  childrenByParent: Map<string, string[]>
): Set<string> {
  if (!activeTargetId) return new Set<string>();
  let rootId: string | null = null;
  for (const n of nodes) {
    const data = n.data as Record<string, unknown> | undefined;
    const isRoot = data?.is_root === true || data?.status === 'captured';
    if (!isRoot) continue;
    const targetId =
      (typeof data?.target_id === 'string' && data.target_id) ||
      (n.identifier.includes(':') ? n.identifier.split(':', 1)[0] : '');
    if (targetId === activeTargetId) {
      rootId = n.identifier;
      break;
    }
  }
  if (!rootId) return new Set<string>();
  return new Set(collectDescendants(rootId, childrenByParent));
}

/**
 * Identifier of the STIX graph's single head node, as a 0-or-1-element set so
 * it plugs into the same auto-collapse machinery as the task-graph passes.
 *
 * STIX is a relationship graph, not a tree, and may expose several nodes with
 * no incoming edge. The head is the no-incoming-edge node whose reachable
 * subtree is largest (the dominant object graph), ties broken by ascending
 * identifier so the choice is deterministic across renders. Returns an empty
 * set when there is nothing sensible to collapse to — no nodes, or a fully
 * cyclic set where every node has an incoming edge and thus no root exists.
 */
export function computeStixHead(
  nodes: GraphNode[],
  childrenByParent: Map<string, string[]>
): Set<string> {
  if (nodes.length === 0) return new Set();

  const incoming = new Set<string>();
  childrenByParent.forEach((kids) => kids.forEach((k) => incoming.add(k)));

  const roots = nodes.filter((n) => !incoming.has(n.identifier));
  if (roots.length === 0) return new Set();

  let bestId = roots[0].identifier;
  let bestReach = collectDescendants(bestId, childrenByParent).length;
  for (const root of roots.slice(1)) {
    const reach = collectDescendants(root.identifier, childrenByParent).length;
    if (
      reach > bestReach ||
      (reach === bestReach && root.identifier < bestId)
    ) {
      bestId = root.identifier;
      bestReach = reach;
    }
  }
  return new Set([bestId]);
}

/**
 * Collect a node and every descendant reachable via ``childrenByParent``
 * (edge-derived adjacency), for recursive collapse/expand. Iterative and
 * visited-guarded, so a cyclic edge set can't spin the stack forever —
 * each id is emitted at most once even if reachable via more than one path.
 */
export function collectDescendants(
  rootId: string,
  childrenByParent: Map<string, string[]>
): string[] {
  const result: string[] = [];
  const visited = new Set<string>();
  const stack: string[] = [rootId];
  while (stack.length) {
    const current = stack.pop()!;
    if (visited.has(current)) continue;
    visited.add(current);
    result.push(current);
    const kids = childrenByParent.get(current) ?? [];
    stack.push(...kids);
  }
  return result;
}

function connectedComponents(g: dagre.graphlib.Graph): string[][] {
  const adj = new Map<string, string[]>();
  g.nodes().forEach((n) => adj.set(n, []));
  g.edges().forEach((e) => {
    adj.get(e.v)?.push(e.w);
    adj.get(e.w)?.push(e.v);
  });
  const components: string[][] = [];
  const visited = new Set<string>();
  for (const start of g.nodes()) {
    if (visited.has(start)) continue;
    const component: string[] = [];
    const stack = [start];
    while (stack.length) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      visited.add(current);
      component.push(current);
      for (const next of adj.get(current) ?? []) {
        if (!visited.has(next)) stack.push(next);
      }
    }
    components.push(component);
  }
  return components;
}

/**
 * Stable structural fingerprint of a graph, for layout memoization. Captures
 * only what changes node POSITIONS or the active spine — the node id set, the
 * edges, the collapsed set, the layout direction, the selected graph, the
 * pending run-task spine, and the active-path node set — and deliberately
 * EXCLUDES presentation data (status / risk / color). So an incremental status
 * tick that doesn't move the active path yields the same signature and skips a
 * dagre relayout, while a structural or active-path change yields a new one
 * (#1128).
 */
export function graphStructureSignature(
  nodes: GraphNode[],
  edges: GraphEdge[],
  collapsed: Set<string>,
  activePathIds: Set<string>,
  direction: 'TB' | 'LR',
  graphIdentifier: string,
  pendingRunTaskNodeId?: string | null
): string {
  const nodeIds = nodes
    .map((n) => n.identifier)
    .sort()
    .join(',');
  const edgePairs = edges
    .filter((e) => e.source && e.target)
    .map((e) => `${e.source}>${e.target}`)
    .sort()
    .join(',');
  const collapsedKey = Array.from(collapsed).sort().join(',');
  const activeKey = Array.from(activePathIds).sort().join(',');
  return [
    graphIdentifier,
    direction,
    pendingRunTaskNodeId ?? '',
    `n:${nodeIds}`,
    `e:${edgePairs}`,
    `c:${collapsedKey}`,
    `a:${activeKey}`
  ].join('|');
}

/**
 * Convert a Crimson Raven graph (cl.Graph.to_dict shape) into React Flow
 * nodes and edges with dagre-assigned positions. Used by the Graph panel
 * every time the selected graph, expansion set, or timeline snapshot
 * changes.
 */
const COMPLETE_EDGE_STYLE = { strokeWidth: 2, stroke: 'hsl(142 72% 40%)' };
const FAILED_EDGE_STYLE = { strokeWidth: 2, stroke: 'hsl(345 82% 42%)' };
const SKIPPED_EDGE_STYLE = { strokeWidth: 2, stroke: 'hsl(27 96% 55%)' };
const ACTIVE_PATH_EDGE_STYLE = { strokeWidth: 2.5, stroke: 'hsl(48 96% 53%)' };
const DEFAULT_EDGE_STYLE = { strokeWidth: 1.5 };

export function buildReactFlowGraph(
  crNodes: GraphNode[],
  crEdges: GraphEdge[],
  collapsed: Set<string>,
  graphIdentifier: string,
  direction: 'TB' | 'LR' = 'LR',
  userActivePathNodeId?: string | null
): { nodes: RFNode<CRNodeData>[]; edges: RFEdge[] } {
  const allIds = new Set(crNodes.map((n) => n.identifier));
  const childrenByParent = new Map<string, string[]>();
  const parentsByChild = new Map<string, string[]>();
  const incoming = new Set<string>();
  for (const edge of crEdges) {
    if (!edge.source || !edge.target) continue;
    if (!allIds.has(edge.source) || !allIds.has(edge.target)) continue;
    if (!childrenByParent.has(edge.source))
      childrenByParent.set(edge.source, []);
    childrenByParent.get(edge.source)!.push(edge.target);
    if (!parentsByChild.has(edge.target)) parentsByChild.set(edge.target, []);
    parentsByChild.get(edge.target)!.push(edge.source);
    incoming.add(edge.target);
  }
  crNodes.forEach((n) => {
    if (Array.isArray(n.subnodes)) {
      for (const child of n.subnodes) {
        incoming.add(child.identifier);
        if (!parentsByChild.has(child.identifier))
          parentsByChild.set(child.identifier, []);
        parentsByChild.get(child.identifier)!.push(n.identifier);
      }
    }
  });

  const flat = flattenByAdjacency(
    crNodes,
    childrenByParent,
    incoming,
    collapsed
  );
  if (flat.length === 0) return { nodes: [], edges: [] };

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: direction,
    nodesep: 60,
    ranksep: 140,
    edgesep: 40,
    marginx: 40,
    marginy: 40
  });
  g.setDefaultEdgeLabel(() => ({}));

  flat.forEach(({ id }) => {
    g.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  const presentIds = new Set(flat.map((f) => f.id));
  const statusById = new Map<string, string | undefined>();
  const activePathIds = new Set<string>();
  flat.forEach(({ id, node }) => {
    const raw = (node.data as any)?.status;
    statusById.set(id, typeof raw === 'string' ? raw.toUpperCase() : undefined);
    if ((node.data as any)?.on_active_path === true) {
      activePathIds.add(id);
    }
  });

  if (userActivePathNodeId && allIds.has(userActivePathNodeId)) {
    const seen = new Set<string>();
    const queue: string[] = [userActivePathNodeId];
    while (queue.length) {
      const current = queue.shift()!;
      if (seen.has(current)) continue;
      seen.add(current);
      activePathIds.add(current);
      const parents = parentsByChild.get(current) ?? [];
      for (const parent of parents) {
        if (!seen.has(parent)) queue.push(parent);
      }
    }
  }

  const rfEdges: RFEdge[] = [];
  const pushEdge = (edge: RFEdge, sourceId: string, targetId: string) => {
    const targetStatus = statusById.get(targetId);
    const complete = targetStatus === 'COMPLETE';
    const terminal = TERMINAL_STATUSES.has(targetStatus ?? '');
    const onActivePath =
      !terminal && activePathIds.has(sourceId) && activePathIds.has(targetId);
    const terminalStyle =
      targetStatus === 'FAILED'
        ? FAILED_EDGE_STYLE
        : targetStatus === 'SKIPPED'
          ? SKIPPED_EDGE_STYLE
          : COMPLETE_EDGE_STYLE;
    const style = terminal
      ? terminalStyle
      : onActivePath
        ? ACTIVE_PATH_EDGE_STYLE
        : DEFAULT_EDGE_STYLE;
    rfEdges.push({
      ...edge,
      style,
      animated: edge.animated || onActivePath,
      data: { ...(edge.data ?? {}), complete, onActivePath }
    });
  };

  crEdges.forEach((edge) => {
    if (!edge.source || !edge.target) return;
    if (!presentIds.has(edge.source) || !presentIds.has(edge.target)) return;
    g.setEdge(edge.source, edge.target);
    pushEdge(
      {
        id: edge.identifier || `${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        label: edge.label && edge.label !== 'None' ? edge.label : undefined,
        type: 'default',
        data: { tooltip: edge.tooltip }
      },
      edge.source,
      edge.target
    );
  });

  flat.forEach(({ id, parentId }) => {
    if (parentId && presentIds.has(parentId)) {
      const edgeId = `subnode-${parentId}-${id}`;
      if (!rfEdges.find((e) => e.id === edgeId)) {
        g.setEdge(parentId, id);
        pushEdge(
          {
            id: edgeId,
            source: parentId,
            target: id,
            type: 'default',
            animated: true,
            data: { tooltip: 'Subnode' }
          },
          parentId,
          id
        );
      }
    }
  });

  const SUPER_ROOT = '__dagre_super_root__';
  const fallbackPos = new Map<string, { x: number; y: number }>();
  let layoutFailed = false;
  try {
    const components = connectedComponents(g);
    if (components.length > 1) {
      g.setNode(SUPER_ROOT, { width: 1, height: 1 });
      for (const component of components) {
        if (component.length > 0)
          g.setEdge(SUPER_ROOT, component[0], { weight: 0 });
      }
    }
    dagre.layout(g);
    if (g.hasNode(SUPER_ROOT)) g.removeNode(SUPER_ROOT);
  } catch {
    layoutFailed = true;
    const cols = Math.max(1, Math.ceil(Math.sqrt(flat.length)));
    flat.forEach(({ id }, i) => {
      fallbackPos.set(id, {
        x: (i % cols) * (NODE_WIDTH + 60) + NODE_WIDTH / 2,
        y: Math.floor(i / cols) * (NODE_HEIGHT + 60) + NODE_HEIGHT / 2
      });
    });
  }

  const rfNodes: RFNode<CRNodeData>[] = flat.map(({ id, node }) => {
    const pos = layoutFailed ? fallbackPos.get(id) : g.node(id);
    const data = node.data ?? {};
    const description =
      typeof (data as any).description === 'string'
        ? (data as any).description
        : undefined;
    const status =
      typeof (data as any).status === 'string'
        ? (data as any).status
        : undefined;
    const riskRaw = (data as any)['Risk Score'] ?? (data as any).risk;
    const risk = typeof riskRaw === 'number' ? riskRaw : undefined;
    const subnodeFromTree = Array.isArray(node.subnodes)
      ? node.subnodes.length
      : 0;
    const subnodeFromEdges = (childrenByParent.get(node.identifier) ?? [])
      .length;
    const subnodeCount = subnodeFromEdges + subnodeFromTree;

    return {
      id,
      type: 'crNode',
      position: {
        x: (pos?.x ?? 0) - NODE_WIDTH / 2,
        y: (pos?.y ?? 0) - NODE_HEIGHT / 2
      },
      data: {
        label: node.name,
        color: node.color || '#3F678D',
        shape: node.shape,
        icon: node.icon,
        risk,
        status,
        description,
        raw: data as Record<string, unknown>,
        hasSubnodes: subnodeCount > 0,
        subnodeCount,
        isExpanded: !collapsed.has(node.identifier),
        graphIdentifier,
        isSelected: false,
        direction
      },
      draggable: true,
      selectable: true
    };
  });

  return { nodes: rfNodes, edges: rfEdges };
}
